import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "../lib/api";
import CandidateLayout from "./CandidateLayout";
import { useAppT } from "../components/home/i18n";
import { Upload, Trash2, FileText, Film, Image as ImageIcon, ExternalLink } from "lucide-react";
import { AuthedImage, AuthedVideo, AuthedFileLink } from "../components/AuthedMedia";

const RULES = {
  cv:           { accept: ".pdf", exts: ["pdf"], maxMB: 5, max: 1, video: false },
  certificate:  { accept: ".pdf,.jpg,.jpeg,.png", exts: ["pdf", "jpg", "jpeg", "png"], maxMB: 10, max: 15, video: false },
  intro_video:  { accept: ".mp4,.mov", exts: ["mp4", "mov"], maxMB: 100, max: 1, video: true, maxDur: 90 },
  work_video:   { accept: ".mp4,.mov", exts: ["mp4", "mov"], maxMB: 150, max: 3, video: true, maxDur: 120 },
};

const ext = (name) => (name.includes(".") ? name.split(".").pop().toLowerCase() : "");
function getDuration(file) {
  return new Promise((resolve) => {
    try {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration || null); };
      v.onerror = () => resolve(null);
      v.src = URL.createObjectURL(file);
    } catch { resolve(null); }
  });
}

function Preview({ m, openLabel }) {
  const e = ext(m.original_filename || "");
  const path = `/media/${m.id}/file`;
  if (["jpg", "jpeg", "png"].includes(e)) return <AuthedImage path={path} className="h-16 w-16 rounded-sm object-cover" alt={m.original_filename} />;
  if (["mp4", "mov"].includes(e)) return <AuthedVideo path={path} className="h-16 w-24 rounded-sm bg-black object-cover" />;
  return <AuthedFileLink path={path} className="inline-flex items-center gap-1 text-xs text-[#1F4A38] underline"><ExternalLink className="h-3.5 w-3.5" /> {openLabel}</AuthedFileLink>;
}

function Uploader({ kind, items, onChange }) {
  const { a } = useAppT();
  const rule = RULES[kind];
  const meta = a.media.rules[kind];
  const ref = useRef();
  const [progress, setProgress] = useState(null);
  const Icon = rule.video ? Film : kind === "certificate" ? ImageIcon : FileText;

  const pick = () => ref.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fe = ext(file.name);
    if (!rule.exts.includes(fe)) { toast.error(`${a.media.unsupported} ${rule.exts.join(", ").toUpperCase()}`); return; }
    if (file.size > rule.maxMB * 1024 * 1024) { toast.error(`${meta.label} ${a.media.exceeds} ${rule.maxMB} MB (${a.media.yoursIs} ${(file.size / 1024 / 1024).toFixed(1)} MB).`); return; }
    if (items.length >= rule.max && rule.max > 1) { toast.error(a.media.maxFiles.replace("{n}", rule.max)); return; }

    let duration = null;
    if (rule.video) {
      duration = await getDuration(file);
      if (duration && rule.maxDur && duration > rule.maxDur) { toast.error(`${a.media.videoIs} ${Math.round(duration)}s — ${a.media.limitIs} ${rule.maxDur}s.`); return; }
    }

    const fd = new FormData();
    fd.append("kind", kind);
    if (duration) fd.append("duration_s", Math.round(duration));
    fd.append("file", file);
    setProgress(0);
    try {
      await API.post("/candidate/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => setProgress(Math.round((p.loaded * 100) / (p.total || 1))),
      });
      toast.success(`${meta.label} ${a.media.uploaded}`);
      onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setProgress(null); }
  };

  const del = async (id) => {
    try { await API.delete(`/candidate/media/${id}`); onChange(); } catch (e) { toast.error(a.media.deleteFailed); }
  };

  return (
    <div className="rounded-md border border-[#E8E4DB] bg-white p-5" data-testid={`uploader-${kind}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2"><Icon className="h-5 w-5 text-[#C4A46B]" /><div><div className="font-serif text-lg text-[#173A2C]">{meta.label}</div><div className="text-[11px] text-[#5C5855]">{meta.note}</div></div></div>
        <button data-testid={`upload-${kind}-btn`} onClick={pick} disabled={progress !== null || (items.length >= rule.max && rule.max > 1)}
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#1F4A38] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><Upload className="h-3.5 w-3.5" /> {a.media.upload}</button>
        <input ref={ref} type="file" accept={rule.accept} className="hidden" data-testid={`file-${kind}`} onChange={onFile} />
      </div>
      {progress !== null && (
        <div className="mt-3"><div className="h-1.5 w-full rounded-full bg-[#E8E4DB]"><div className="h-1.5 rounded-full bg-[#C4A46B] transition-all" style={{ width: `${progress}%` }} /></div><div className="mt-1 text-[11px] text-[#5C5855]">{a.media.uploading} {progress}%</div></div>
      )}
      <div className="mt-3 space-y-2">
        {items.length === 0 && <div className="text-sm text-[#8a8580]">{a.media.noFiles}</div>}
        {items.map((m) => (
          <div key={m.id} data-testid={`media-item-${m.id}`} className="flex items-center gap-3 rounded-sm border border-[#E8E4DB] p-2">
            <Preview m={m} openLabel={a.media.openPdf} />
            <div className="flex-1 min-w-0"><div className="truncate text-sm text-[#2C2A29]">{m.original_filename}</div><div className="text-[11px] text-[#5C5855]">{(m.size / 1024 / 1024).toFixed(2)} MB{m.duration_s ? ` · ${Math.round(m.duration_s)}s` : ""}</div></div>
            <button data-testid={`delete-media-${m.id}`} onClick={() => del(m.id)} className="text-[#8B3A3A]"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MediaManager() {
  const { a } = useAppT();
  const [media, setMedia] = useState([]);
  const load = async () => { const { data } = await API.get("/candidate/media"); setMedia(data.media); };
  useEffect(() => { load(); }, []);
  const byKind = (k) => media.filter((m) => m.kind === k);

  return (
    <CandidateLayout>
      <h1 className="font-serif text-3xl text-[#173A2C]">{a.media.title}</h1>
      <p className="mt-1 text-sm text-[#5C5855]">{a.media.sub}</p>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Uploader kind="cv" items={byKind("cv")} onChange={load} />
        <Uploader kind="certificate" items={byKind("certificate")} onChange={load} />
        <Uploader kind="intro_video" items={byKind("intro_video")} onChange={load} />
        <Uploader kind="work_video" items={byKind("work_video")} onChange={load} />
      </div>
    </CandidateLayout>
  );
}
